// Harbinger pure helpers. No React, no Three.js, no DOM.
//
// ── Codex (Harbinger) ──
//   "Tap → Play or draw a site.
//    On setup, determine three random squares. Once on your turn,
//    you can cast a minion to one of them and for (1) less."
//
// The first line is the default avatar tap (handled by the engine).
// The setup line is modeled as a one-shot MODIFY_CAST_COST ledger
// entry installed when Harbinger enters play. The entry carries the
// three picked cells in args.cells; the ledger's matchesCostFilter
// gates by candidate type (Minion) AND destination cell.
//
// The same ledger entry is also the source of truth for the visual
// summoning-circle layer. The renderer scans the ledger for entries
// keyed by this op's cells filter and draws a circle per marked cell.

import { OP } from '../../schema';
import { matchesFilter } from '../../cardFilter';
import { countsAsMinion } from '../../../core/typeline';

export const HARBINGER_AVATAR_ID = 'sorcery-harbinger';
export const HARBINGER_MARK_COUNT = 3;
export const HARBINGER_COST_DELTA = -1;

/**
 * Pick `count` distinct random cells inside a `cols × rows` grid.
 * Returned as `[{ col, row }, ...]` in the order they were drawn.
 *
 * `rng` is a `() => [0, 1)` function. Pass a seeded RNG when you
 * need determinism (multiplayer broadcast of the picked set bypasses
 * the need to seed. Only the chooser rolls, the receiver imports).
 * Defaults to `Math.random` for the casual / single-client case.
 */
export function pickHarbingerMarks(cols, rows, count = HARBINGER_MARK_COUNT, rng = Math.random) {
  const total = cols * rows;
  if (cols <= 0 || rows <= 0 || count <= 0) return [];
  const picks = Math.min(count, total);
  const seen = new Set();
  const out = [];
  while (out.length < picks) {
    const idx = Math.floor(rng() * total);
    if (seen.has(idx)) continue;
    seen.add(idx);
    out.push({ col: idx % cols, row: Math.floor(idx / cols) });
  }
  return out;
}

/**
 * Build the ledger-entry payload that grants the Harbinger discount.
 * Caller (GameBoard) hands the returned object to `effectLedger.add`
 * and broadcasts via `sync.effectAdded`.
 */
export function buildHarbingerLedgerEntry(avatarCard, marks) {
  const controller = avatarCard.rotated ? 'p2' : 'p1';
  return {
    sourceCardId: avatarCard.id,
    sourceCardName: avatarCard.name || 'Harbinger',
    controller,
    targetPlayer: controller,
    effect: {
      op: OP.MODIFY_CAST_COST,
      args: {
        delta: HARBINGER_COST_DELTA,
        max_uses_per_turn: 1,
        filter: {
          type: 'Minion',
          cells: marks.map((m) => ({ col: m.col, row: m.row })),
        },
      },
    },
    expiry: { type: 'until_leaves' },
  };
}

/**
 * Pull the picked cells off whatever ledger entry installed them.
 * Returns the cell list or null if the entry is not a Harbinger one.
 */
export function marksFromLedgerEntry(entry) {
  if (!entry || entry.effect?.op !== OP.MODIFY_CAST_COST) return null;
  const cells = entry.effect.args?.filter?.cells;
  if (!Array.isArray(cells) || cells.length === 0) return null;
  return cells;
}

/**
 * Check whether (col, row) is one of the marked cells. Used by the
 * cost-modifier filter at cast time.
 */
export function isMarkedCell(cells, col, row) {
  if (!Array.isArray(cells)) return false;
  for (const c of cells) {
    if (c.col === col && c.row === row) return true;
  }
  return false;
}

/**
 * v1.111. Return the set of cells (as `'col,row'` keys) that
 * `controller`'s Harbinger ledger entry currently grants as legal
 * minion-summon destinations. Codex text: "Once on your turn, you
 * can cast a minion to one of them and for (1) less." → the legal-
 * cell extension AND the discount are gated by the same once-per-
 * turn use. We therefore include the marks ONLY while the cost-
 * modifier still has a remaining use this turn (entry.usesThisTurn
 * < entry.effect.args.max_uses_per_turn). Cards of any non-Minion
 * type fall through to the empty set.
 *
 * Pure: takes the ledger entries array and the candidate's type;
 * returns a Set ready to be unioned with `spawnableCellsForMinion`.
 */
export function harbingerExtraSpawnCells(ledgerEntries, controller, cardType, fullCard = null) {
  const out = new Set();
  if (cardType !== 'Minion' && !countsAsMinion(fullCard)) return out;
  if (!Array.isArray(ledgerEntries)) return out;
  for (const entry of ledgerEntries) {
    if (entry?.targetPlayer !== controller) continue;
    const marks = marksFromLedgerEntry(entry);
    if (!marks) continue;
    const filter = entry.effect?.args?.filter;
    if (filter?.type && filter.type !== cardType) continue;
    // When the caller supplies the candidate card, honour the FULL
    // filter (subtype etc.) so a Dragons-only mark (Caelestis's
    // "next Dragon you cast here costs (0)") doesn't light up for
    // non-Dragon minions. The first mark satisfies the filter's own
    // `cells` requirement.
    if (fullCard && !matchesFilter(fullCard, filter, { destinationCell: marks[0] })) continue;
    const max = entry.effect?.args?.max_uses_per_turn;
    if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
    for (const m of marks) out.add(`${m.col},${m.row}`);
  }
  return out;
}

/**
 * The subset of cell-discount marks that come from a DRAGON PORTAL
 * source (Dragonlord's Lair / Caelestis) rather than a Harbinger
 * mark. The entry's filter pins `subtype: 'dragon'`. Same matching
 * as harbingerExtraSpawnCells, narrowed to dragon entries, so the
 * drag-reach overlay can paint these cells teal (matching the dragon
 * portal shader) instead of Harbinger purple.
 */
export function dragonPortalMarkCells(ledgerEntries, controller, fullCard = null) {
  const out = new Set();
  if (!Array.isArray(ledgerEntries)) return out;
  for (const entry of ledgerEntries) {
    if (entry?.targetPlayer !== controller) continue;
    const filter = entry.effect?.args?.filter;
    if (String(filter?.subtype || '').toLowerCase() !== 'dragon') continue;
    const marks = marksFromLedgerEntry(entry);
    if (!marks) continue;
    if (fullCard && !matchesFilter(fullCard, filter, { destinationCell: marks[0] })) continue;
    const max = entry.effect?.args?.max_uses_per_turn;
    if (typeof max === 'number' && (entry.usesThisTurn || 0) >= max) continue;
    for (const m of marks) out.add(`${m.col},${m.row}`);
  }
  return out;
}
